// Modules
import { useCallback } from 'react'
import { useApolloClient } from '@apollo/client'

// Application
import {
    AuthenticateWithReCaptcha,
    AuthenticateCIPWithRecaptcha,
    AuthenticateHash,
    AuthenticateUniqueToken,
    GenerateUniqueToken,
    UpdatePatientConfig,
    LogoutAccount,
    AuthenticateWithLink,
} from 'services/gql/auth/mutation'

import { getLabsViewer, GetMagicLinkById, getReportsHistory } from 'services/gql/auth/query'
import { getUserIdJwt } from 'utils/jwt'
import {
    AuthenticationPayload,
    IntegratedAuthTokenResponse,
    AuthenticateData,
    AuthenticateCIPData,
    SignInCredentials,
    IntegratedAuthToken,
    SignInCIPCredentials,
    Lab,
    AuthenticateWithUniqueTokenData,
    AuthenticateWithUniqueTokenResponse,
    GenerateUniqueTokenData,
    GenerateUniqueTokenProps,
    GenerateUniqueResponse,
    LogoutData,
    LogoutResponse,
    GetMagicLinkByIdType,
} from 'typings/auth'

export const useLoginService = () => {
    const client = useApolloClient()

    const authenticate = async ({
        username,
        password,
        recaptchaToken,
        keepLogged,
        logInfo,
    }: SignInCredentials): Promise<AuthenticationPayload | null> => {
        const { data } = await client.mutate<AuthenticateData>({
            mutation: AuthenticateWithReCaptcha,
            variables: {
                username,
                password,
                recaptchaToken,
                uniqueAccess: true,
                keepLogged,
                logInfo,
            },
        })

        if (data?.authenticateWithReCaptcha.access_token) {
            return {
                accessToken: data.authenticateWithReCaptcha.access_token,
                refreshToken: data.authenticateWithReCaptcha.refresh_token,
                userId: getUserIdJwt(data.authenticateWithReCaptcha.access_token),
                uuid: data.authenticateWithReCaptcha?.uuid,
            }
        }

        return null
    }

    const authenticateWithLink = async ({ federalTaxId, magicLinkId }: { federalTaxId: string; magicLinkId: string }) => {
        const { data } = await client.mutate({
            mutation: AuthenticateWithLink,
            variables: {
                federalTaxId,
                magicLinkId,
            },
        })

        if (data?.authenticateWithMagicLink?.accessToken) {
            return {
                accessToken: data.authenticateWithMagicLink.accessToken,
                refreshToken: data.authenticateWithMagicLink.refreshToken,
            }
        }

        return data
    }

    const logoutUser = async (uuid: string): Promise<LogoutResponse> => {
        const { data } = await client.mutate<LogoutData>({
            mutation: LogoutAccount,
            variables: {
                uuid,
            },
        })

        return data?.logoutAccount || null
    }

    const authenticateWithHash = async (hash: String): Promise<IntegratedAuthTokenResponse> => {
        const { data } = await client.mutate<IntegratedAuthToken>({
            mutation: AuthenticateHash,
            variables: {
                hash,
            },
        })

        return data?.authenticateHash || null
    }

    const authenticateWithUniqueToken = async (uniqueToken: string): Promise<AuthenticateWithUniqueTokenResponse> => {
        const { data } = await client.mutate<AuthenticateWithUniqueTokenData>({
            mutation: AuthenticateUniqueToken,
            variables: {
                uniqueToken,
            },
        })
        return data?.authenticateUniqueToken || null
    }

    const generateUniqueToken = async ({ accessToken, refreshToken }: GenerateUniqueTokenProps): Promise<GenerateUniqueResponse> => {
        const { data } = await client.mutate<GenerateUniqueTokenData>({
            mutation: GenerateUniqueToken,
            variables: { accessToken, refreshToken },
        })

        return data?.generateUniqueToken as GenerateUniqueResponse
    }

    const authenticateCIP = async (credentials: SignInCIPCredentials): Promise<AuthenticationPayload | null> => {
        const { data } = await client.mutate<AuthenticateCIPData>({
            mutation: AuthenticateCIPWithRecaptcha,
            variables: credentials,
        })
        if (data?.authenticateCIPWithReCaptcha?.accessToken) {
            return {
                accessToken: data?.authenticateCIPWithReCaptcha?.accessToken,
                refreshToken: '',
                userId: getUserIdJwt(data?.authenticateCIPWithReCaptcha?.accessToken),
            }
        }

        return null
    }

    const getMagicLinkById = async (id: string): Promise<GetMagicLinkByIdType | null> => {
        const { data } = await client.query<{ getMagicLinkById: GetMagicLinkByIdType }>({
            query: GetMagicLinkById,
            variables: { id },
        })

        return data?.getMagicLinkById
    }

    const getLabs = async (): Promise<Lab[]> => {
        const { data } = await client.query<{ labs: Lab[] }>({
            query: getLabsViewer,
        })

        return data.labs
    }

    const reportsHistory = (): void => {
        try {
            client.query({
                query: getReportsHistory,
                fetchPolicy: 'no-cache',
            })
        } catch (e) {
            console.error('error on reportsHistory')
        }

        return
    }

    const updatePatientConfig = useCallback(
        async (key: string, value: boolean = true): Promise<void> => {
            await client.mutate<{ updatePatientConfig: { status: string } }>({
                mutation: UpdatePatientConfig,
                variables: {
                    key: key,
                    value,
                },
            })
        },
        [client],
    )

    return {
        authenticate,
        authenticateCIP,
        authenticateWithHash,
        authenticateWithUniqueToken,
        authenticateWithLink,
        getLabs,
        generateUniqueToken,
        reportsHistory,
        updatePatientConfig,
        logoutUser,
        getMagicLinkById,
    }
}
